import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import { View } from 'react-native'

import type { LayoutRectangle } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'

import {
  XStack,
  YStack,
  ThemeableStack,
  styled,
  AnimatePresence,
  TamaguiElement,
  useMedia,
  View as TamaguiView,
} from 'tamagui'

import { useLocation } from 'react-router-dom'

import { ServerContext } from '@data/ServerContext'

import { Text } from '../Text'
import { Button } from '../Button/Button'
import { Icon } from '../Icon'

import { Item, DynamicIslandRowsContext } from './DynamicIslandProvider'

const SPRING_OPTIONS = { mass: 0.15, stiffness: 60, damping: 5 }

type WebLayoutRectangle = LayoutRectangle & {
  left: number
  top: number
}

const useIsomorphicLayoutEffect = __SERVER__ ? useEffect : useLayoutEffect

export const DynamicIsland = ({ isLoading }: { isLoading: boolean }) => {
  const dynamicIslandRows = useContext(DynamicIslandRowsContext)

  const [contentBackgroundPosition, setContentBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >()

  const [backgroundPosition, setBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >(contentBackgroundPosition)

  const [appliedBackgroundPosition, setAppliedBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >(undefined)

  const media = useMedia()
  const serverContext = useContext(ServerContext)

  const backgroundLeft = useSharedValue(0)
  const backgroundTop = useSharedValue(0)
  const backgroundWidth = useSharedValue(
    (serverContext && serverContext.defaultBreakpoint > 0) ||
      (!serverContext && media.gtXs)
      ? 407
      : 237
  )
  const backgroundHeight = useSharedValue(48)

  const animatedContentStyle = useAnimatedStyle(() => {
    if (contentBackgroundPosition) {
      return {
        clipPath: `inset(1px ${contentBackgroundPosition.left + contentBackgroundPosition.width + 2 - (contentBackgroundPosition.left + backgroundLeft.value + backgroundWidth.value)}px ${-(
          backgroundHeight.value -
          contentBackgroundPosition.height -
          1
        )}px ${backgroundLeft.value + 1}px)`,
        willChange: 'clip-path,transform',
      }
    } else {
      return {
        clipPath: `inset(0px 0px 0px 0px)`,
        willChange: 'clip-path,transform',
      }
    }
  }, [contentBackgroundPosition, backgroundPosition])

  const backgroundStyle = useAnimatedStyle(() => {
    return {
      width: backgroundWidth.value,
      height: backgroundHeight.value,
      transform: [
        { translateX: backgroundLeft.value },
        { translateY: backgroundTop.value },
      ],
      willChange: 'transform,width,height,background-color',
    }
  }, [contentBackgroundPosition, backgroundPosition])

  const dynamicIslandView = useRef<TamaguiElement>()

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handler = () => {
      requestAnimationFrame(() => {
        if (dynamicIslandView.current) {
          const contentLayout =
            dynamicIslandView.current.getBoundingClientRect()
          const parentLayout =
            dynamicIslandView.current.parentNode?.getBoundingClientRect()

          const dynamicIslandPosition = {
            x: contentLayout.x - (parentLayout?.x || 0),
            y: contentLayout.y - (parentLayout?.y || 0),
            width: contentLayout.width,
            height: contentLayout.height,
            left: contentLayout.left,
            top: contentLayout.top,
          }

          setContentBackgroundPosition(dynamicIslandPosition)

          if (!backgroundPosition) {
            setAppliedBackgroundPosition(dynamicIslandPosition)

            backgroundWidth.set(dynamicIslandPosition.width)
            backgroundHeight.set(dynamicIslandPosition.height)
          }
        }
      })
    }

    if (!contentBackgroundPosition) {
      handler()
    }

    window.addEventListener('resize', handler)

    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [backgroundPosition, contentBackgroundPosition])

  const [isHovered, setIsHovered] = useState(false)

  return (
    <DynamicIslandContainer>
      <TamaguiView
        ref={dynamicIslandView}
        onHoverIn={() => {
          if (!media.hoverNone) {
            setIsHovered(true)
          }
        }}
        onHoverOut={() => {
          if (!media.hoverNone) {
            setIsHovered(false)
          }
        }}
      >
        <DynamicIslandBackground
          style={backgroundStyle}
          animation='dynamicIsland'
          animateOnly={['backgroundColor']}
          backgroundColor={
            isHovered || backgroundPosition
              ? 'rgba(255,255,255,0.9)'
              : 'rgba(255,255,255,0.75)'
          }
        />

        <AnimatePresence>
          {dynamicIslandRows.rows.map((islandItems, index) => {
            return (
              <DynamicIslandRow
                key={index}
                // animation='slow'
                // transform={[
                //   {
                //     rotateX: '0deg',
                //   },
                //   {
                //     translateY: 0,
                //   },
                // ]}
                // opacity={1}
                // enterStyle={{
                //   transform: [
                //     // { rotateX: '90deg' },
                //     {
                //       translateY:
                //         (contentBackgroundPosition?.height || 0) / 2,
                //     },
                //   ],
                //   opacity: 0,
                // }}
                // backgroundColor='pink'
                // enterStyle={{ backgroundColor: 'red' }}
                gap='$2'
                alignItems='center'
                padding='$2'
                borderRadius={24}
                islandItems={islandItems}
                isLoading={isLoading}
                backgroundPosition={appliedBackgroundPosition}
                setBackgroundPosition={(newBackgroundPosition) => {
                  if (newBackgroundPosition) {
                    const newPosition = newBackgroundPosition

                    setBackgroundPosition(newPosition)

                    setAppliedBackgroundPosition(newPosition)

                    backgroundTop.value = withSpring(
                      newPosition.y,
                      SPRING_OPTIONS
                    )
                    backgroundLeft.value = withSpring(
                      newPosition.x,
                      SPRING_OPTIONS
                    )
                    backgroundHeight.value = withSpring(
                      newPosition.height,
                      SPRING_OPTIONS
                    )
                    backgroundWidth.value = withSpring(
                      newPosition.width,
                      SPRING_OPTIONS
                    )
                  } else if (contentBackgroundPosition) {
                    // console.log(
                    //   'SETTING APPLIED BACKGROUND TO CONTENT BACKGROUND'
                    // )

                    setBackgroundPosition(undefined)

                    const contentLayout =
                      dynamicIslandView.current?.getBoundingClientRect()
                    const parentLayout =
                      dynamicIslandView.current?.parentNode?.getBoundingClientRect()

                    const dynamicIslandPosition = {
                      x: contentLayout.x - (parentLayout?.x || 0),
                      y: contentLayout.y - (parentLayout?.y || 0),
                      width: contentLayout.width,
                      height: contentLayout.height,
                      left: contentLayout.left,
                      top: contentLayout.top,
                    }

                    setContentBackgroundPosition(dynamicIslandPosition)

                    setAppliedBackgroundPosition({
                      ...dynamicIslandPosition,
                      x: 0,
                      y: 0,
                    })

                    backgroundTop.value = withSpring(0, SPRING_OPTIONS)
                    backgroundLeft.value = withSpring(0, SPRING_OPTIONS)
                    backgroundHeight.value = withSpring(
                      dynamicIslandPosition.height,
                      SPRING_OPTIONS
                    )
                    backgroundWidth.value = withSpring(
                      dynamicIslandPosition.width,
                      SPRING_OPTIONS
                    )
                  } else {
                    // console.log('CLEARING APPLIED BACKGROUND')

                    setBackgroundPosition(undefined)
                    setAppliedBackgroundPosition(undefined)
                  }
                }}
                style={animatedContentStyle}
              />
            )
          })}
        </AnimatePresence>
      </TamaguiView>
    </DynamicIslandContainer>
  )
}

const DynamicIslandRow = Animated.createAnimatedComponent(
  XStack.styleable<{
    islandItems: Array<Item>
    backgroundPosition?: WebLayoutRectangle
    isLoading: boolean
    setBackgroundPosition: (
      newBackgroundPosition?: WebLayoutRectangle
    ) => unknown
  }>(
    (
      {
        islandItems,
        backgroundPosition,
        setBackgroundPosition,
        isLoading,
        ...props
      },
      ref
    ) => {
      const location = useLocation()

      const [selectedItemId, setSelectedItemId] = useState<
        Item['id'] | undefined
      >()

      const [isHovered, setIsHovered] = useState(false)

      // if we navigate to a new page, reset the dynamic island
      useEffect(() => {
        setSelectedItemId(undefined)
        setBackgroundPosition(undefined)
      }, [location])

      // reset dynamic island after 5 seconds of inactivity
      useEffect(() => {
        if (selectedItemId && !isHovered) {
          const timeout = setTimeout(() => {
            setSelectedItemId(undefined)
            setBackgroundPosition(undefined)
          }, 5000)

          return () => {
            clearTimeout(timeout)
          }
        }
      }, [selectedItemId, isHovered])

      const selectedItemIndex = useMemo(() => {
        const index = islandItems.findIndex(
          (item) => item.id === selectedItemId
        )

        if (index >= 0) {
          return index
        }
      }, [islandItems, selectedItemId])

      const selectedItem = useMemo(() => {
        if (selectedItemIndex === undefined) {
          return
        }

        return islandItems[selectedItemIndex]
      }, [islandItems, selectedItemIndex])

      const media = useMedia()

      return (
        <XStack
          {...props}
          onHoverIn={() => {
            if (!media.hoverNone) {
              setIsHovered(true)
            }
          }}
          onHoverOut={() => {
            if (!media.hoverNone) {
              setIsHovered(false)
            }
          }}
          ref={ref}
        >
          <Text
            animation='dynamicIsland'
            fontWeight='$6'
            position='absolute'
            transform={[
              {
                translateX: selectedItem
                  ? (backgroundPosition?.x || 0) + (4 * 4) / 2
                  : 0,
              },
            ]}
            opacity={selectedItem ? 1 : 0}
            display={selectedItem ? 'flex' : 'none'}
            style={{ willChange: 'transform,opacity' }}
          >
            {selectedItem ? selectedItem.name : null}
          </Text>

          {islandItems.map((item, index) => {
            return (
              <DynamicIslandItem
                {...item}
                key={item.id}
                isLoading={isLoading}
                itemIndex={index}
                selectedItem={selectedItem}
                selectedItemIndex={selectedItemIndex}
                setSelectedItemId={setSelectedItemId}
                setBackgroundPosition={(newBackgroundPosition) => {
                  setBackgroundPosition(newBackgroundPosition)
                }}
              />
            )
          })}
        </XStack>
      )
    }
  )
)

const DynamicIslandItem = ({
  id,
  name,
  component,
  child,
  selectedItem,
  selectedItemIndex,
  setSelectedItemId,
  setBackgroundPosition,
  itemIndex,
  containerStyle = {},
  isLoading,
}: {
  isLoading: boolean
  selectedItem: Item | undefined
  selectedItemIndex: number | undefined
  setBackgroundPosition: (newBackgroundPosition?: WebLayoutRectangle) => unknown
  setSelectedItemId: (newSelectedItem: Item['id'] | undefined) => unknown
  itemIndex: number
} & Item) => {
  const [itemLayout, setItemLayout] = useState<WebLayoutRectangle>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  })

  const [expandLayout, setExpandLayout] = useState<WebLayoutRectangle>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  })

  const [isHovered, setIsHovered] = useState(false)

  const itemRef = useRef<TamaguiElement>(null)
  const closeItemRef = useRef<TamaguiElement>(null)

  const translateX = useMemo(() => {
    if (selectedItem === undefined || selectedItemIndex === undefined) {
      return 0
    }

    if (selectedItem.id === id) {
      return 0
    }

    // item is to the right of selection
    if (selectedItemIndex < itemIndex) {
      return itemLayout.left
      // return 0
    }

    return -itemLayout.left - itemLayout.width
  }, [selectedItem, selectedItemIndex, itemIndex, itemLayout])

  const calculateBackgroundPosition = (
    itemLayout: LayoutRectangle & { top: number; left: number },
    expandLayout: LayoutRectangle & { top: number; left: number }
  ): LayoutRectangle & { top: number; left: number } => {
    return {
      top: itemLayout.top - 4 * 2,
      height: itemLayout.height + expandLayout.height + 4 * 2,

      left: itemLayout.left + itemLayout.width + 4 * 2 - expandLayout.width,
      width: expandLayout.width,

      x: itemLayout.x + itemLayout.width - expandLayout.width + (4 * 4) / 2,
      y: 0,
    }
  }

  // onLayout isn't reliable via Tamagui
  // Need to measure layout manually
  useIsomorphicLayoutEffect(() => {
    if (selectedItem?.id === id) {
      setItemLayout((itemLayout) => {
        try {
          if (itemRef.current) {
            const layout = itemRef.current.getBoundingClientRect()
            const parentLayout =
              itemRef.current.parentNode?.getBoundingClientRect()

            const computedLayout = {
              x: layout.x - (parentLayout?.x || 0),
              y: layout.y - (parentLayout?.y || 0),
              width: layout.width,
              height: layout.height,
              left: layout.left,
              top: layout.top,
            }

            return computedLayout
          }

          return itemLayout
        } catch (err) {
          return itemLayout
        }
      })
    }
  }, [selectedItem])

  useIsomorphicLayoutEffect(() => {
    if (selectedItem?.id === id) {
      if (itemLayout.width === 0 && itemLayout.height === 0) {
        setSelectedItemId(undefined)
        setBackgroundPosition(undefined)
      } else {
        setBackgroundPosition(
          calculateBackgroundPosition(itemLayout, expandLayout)
        )
      }
    }
  }, [selectedItem, id, itemLayout, expandLayout])

  const renderedComponent = useMemo(() => {
    if (typeof component === 'function') {
      const Comp = component

      return <Comp isLoading={isLoading} />
    }

    return component
  }, [component, isLoading])

  if (name && child) {
    return (
      <>
        <YStack
          {...containerStyle}
          key={id}
          ref={itemRef}
          animation='dynamicIsland'
          transform={[
            {
              translateX,
            },
            { rotateZ: selectedItem?.id === id ? '45deg' : '0deg' },
          ]}
          opacity={selectedItem === undefined || selectedItem.id === id ? 1 : 0}
          cursor='pointer'
          onPress={() => {
            if (selectedItem?.id === id) {
              setSelectedItemId(undefined)
              setBackgroundPosition(undefined)
            } else {
              setSelectedItemId(id)
              // setBackgroundPosition(
              //   calculateBackgroundPosition(itemLayout, expandLayout)
              // )
            }
          }}
          onHoverIn={() => {
            setIsHovered(true)
          }}
          onHoverOut={() => {
            setIsHovered(false)
          }}
          onLayout={(event) => {
            // console.log('ITEM LAYOUT', id, event.nativeEvent.layout)
            setItemLayout(event.nativeEvent.layout as WebLayoutRectangle)
          }}
          style={{ willChange: 'transform,opacity' }}
        >
          <YStack
            animation='dynamicIsland'
            opacity={selectedItem?.id === id ? 0 : 1}
          >
            {renderedComponent}
          </YStack>

          <Button
            ref={closeItemRef}
            position='absolute'
            animation='dynamicIsland'
            opacity={selectedItem?.id === id ? 1 : 0}
            minHeight='$8'
            height='$8'
            width='$8'
            padding='$0'
            alignItems='center'
            justifyContent='center'
            style={{
              visibility: selectedItem?.id === id ? 'visible' : 'hidden',
            }}
            secondary
          >
            <Icon name='add' />
          </Button>
        </YStack>

        <YStack
          minWidth={120}
          animation='dynamicIsland'
          position='absolute'
          top={itemLayout.height + 2 * 4}
          transform={[
            {
              translateX:
                selectedItem?.id === id
                  ? itemLayout.x + itemLayout.width - expandLayout.width
                  : -itemLayout.width + 4 * 4,
            },
          ]}
          opacity={selectedItem?.id === id ? 1 : 0}
          paddingVertical='$2'
          paddingHorizontal='$4'
          // paddingTop='$2'
          // paddingBottom='$4'
          style={{ visibility: selectedItem?.id === id ? 'visible' : 'hidden' }}
          onLayout={(event) => {
            // console.log('EXPAND LAYOUT', event.nativeEvent.layout)

            setExpandLayout(event.nativeEvent.layout as WebLayoutRectangle)
          }}
          // backgroundColor={selectedItem?.id === id ? 'purple' : undefined}
        >
          {child}
        </YStack>
      </>
    )
  }

  return (
    <YStack
      {...containerStyle}
      key={id}
      animation='dynamicIsland'
      transform={[
        {
          translateX,
        },
      ]}
      opacity={selectedItem === undefined || selectedItem.id === id ? 1 : 0}
      onLayout={(event) => {
        // console.log('ITEM LAYOUT', event.nativeEvent.layout)
        setItemLayout(event.nativeEvent.layout as WebLayoutRectangle)
      }}
    >
      {renderedComponent}
    </YStack>
  )
}

const DynamicIslandContainer = styled(View, {
  position: 'fixed',
  zIndex: 100,
  top: 12,
  // top: 220,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
})

const DynamicIslandBackground = Animated.createAnimatedComponent(
  styled(ThemeableStack, {
    // animation: 'dynamicIsland',

    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    // overflow: 'hidden',
    boxShadow:
      'rgba(27, 19, 64, 0.08) 0px 8px 8px -2px, rgba(27, 19, 64, 0.05) 0px 15px 25px 10px',
    position: 'absolute',
    borderRadius: 24,
    borderColor: 'rgba(19, 0, 102, 0.08)',
    borderWidth: 1,
    //   zIndex: 0,

    backdropFilter: 'blur(8px) saturate(250%)',

    //   height: 48,
    //   width: 418,

    style: {
      WebkitBackdropFilter: 'blur(8px) saturate(250%)',
      // willChange: 'top,left,width,height',
      willChange: 'transform,width,height',
    },
  } as const)
)
